import React, { Fragment } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import {
  PUSH_SELECT_ALL_COUNTRIES,
  emailUserTargetTypes,
} from "./notificationConstant";
import {
  getPushCountryCodes,
  normalizePushCountrySelection,
} from "./notificationFormUtils";
import { getAllowedCountrySelectOptions } from "../../utils/general";
import { selectMenuProps } from "../../utils/selectMenuProps";
import { useDesignTokens } from "../../theme/useDesignTokens";
import FormInput from "../Common/FormInputs/MuiTextField";
import {
  emailCompactFormControlSx,
  emailFieldRowSx,
  emailFocusOutlineSx,
  emailSectionCardSx,
} from "./emailFormStyles";

const EmailUserSelection = ({
  userTargetType,
  handleUserTargetTypeChange,
  countries = [],
  handleUserCountries,
  allowedCountries = [],
  userIdCSVFileInputRef,
  handleUserIdFileInput,
  uploadedUserIdFileInput = "",
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = emailFocusOutlineSx(primaryColor);
  const controlSx = emailCompactFormControlSx(theme);

  const countryOptions = getAllowedCountrySelectOptions(allowedCountries);
  const allCountryCodes = getPushCountryCodes(allowedCountries);
  const selectedCountries = Array.isArray(countries) ? countries : [];
  const allSelected =
    allCountryCodes.length > 0 &&
    allCountryCodes.every((code) => selectedCountries.includes(code));
  const isBroadcast = userTargetType === emailUserTargetTypes.broadcast.value;
  const isTargeted = userTargetType === emailUserTargetTypes.targeted.value;

  const handleCountryChange = (event) => {
    handleUserCountries?.(
      normalizePushCountrySelection(event.target.value, allCountryCodes)
    );
  };

  return (
    <Fragment>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
        Users
      </Typography>
      <Paper sx={emailSectionCardSx}>
        <Box sx={{ ...emailFieldRowSx, alignItems: { xs: "start", md: "center" } }}>
          <Typography variant="body2" sx={{ fontWeight: 400 }}>
            User Type:
          </Typography>
          <Box>
            <RadioGroup
              row
              value={userTargetType}
              onChange={(e) => handleUserTargetTypeChange(e.target.value)}
              sx={{ gap: { xs: 0, sm: 2 }, flexWrap: "wrap" }}
            >
              <FormControlLabel
                value={emailUserTargetTypes.broadcast.value}
                control={<Radio size="small" color="primary" />}
                label={emailUserTargetTypes.broadcast.label}
              />
              <FormControlLabel
                value={emailUserTargetTypes.targeted.value}
                control={<Radio size="small" color="primary" />}
                label={emailUserTargetTypes.targeted.label}
              />
            </RadioGroup>
          </Box>
        </Box>

        {isBroadcast && (
          <Box sx={emailFieldRowSx}>
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              Country:
              <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                *
              </Typography>
            </Typography>
            <Box sx={controlSx}>
              <FormInput
                id="select-outlined-email-country-name-id"
                type="select"
                multiple
                size="small"
                fullWidth
                name="country"
                value={selectedCountries}
                onChange={handleCountryChange}
                displayEmpty
                renderValue={(selected) => {
                  const values = Array.isArray(selected) ? selected : [];
                  if (!values.length) return "Select Country";
                  if (
                    allCountryCodes.length > 0 &&
                    allCountryCodes.every((code) => values.includes(code))
                  ) {
                    return "All";
                  }
                  return values.join(", ");
                }}
                MenuProps={selectMenuProps}
                options={[
                  { value: PUSH_SELECT_ALL_COUNTRIES, label: "Select All" },
                  ...countryOptions,
                ]}
                renderOption={(option) => {
                  const isSelectAll = option.value === PUSH_SELECT_ALL_COUNTRIES;
                  const checked = isSelectAll
                    ? allSelected
                    : selectedCountries.includes(option.value);
                  return (
                    <MenuItem key={option.value} value={option.value} dense>
                      <Checkbox checked={checked} size="small" sx={{ p: 0.5, mr: 1 }} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  );
                }}
                sx={focusOutlineSx}
              />
            </Box>
          </Box>
        )}

        {isTargeted && (
          <Box sx={emailFieldRowSx}>
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              Upload User Ids CSV File:
            </Typography>
            <Box sx={controlSx}>
              <FormInput
                type="file"
                size="small"
                fullWidth
                accept=".csv"
                name={uploadedUserIdFileInput?.name ?? uploadedUserIdFileInput}
                inputRef={userIdCSVFileInputRef}
                onChange={(e) => handleUserIdFileInput(e.target.files?.[0])}
                sx={focusOutlineSx}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Fragment>
  );
};

export default EmailUserSelection;
